import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { useAppState } from "../AppState";
import { api } from "../lib/api";
import { AddrLink, BLOCK_STATUS, BlockLink, ErrorMsg, LiveBadge, Loading, PageHeader, Segmented, SlotPill } from "../components/Bits";
import { formatRelative, slotTimestampMs } from "../lib/format";
import { useChainParams } from "../hooks/useChainParams";
import { Paginator, usePaged } from "../components/Paginator";
import type { StoredBlock } from "../lib/types";

// ponytail: mainnet ENDORSEMENT_COUNT, read it from chain params if it ever varies per network.
const ENDORSEMENTS_PER_BLOCK = 16;

const FILTERS = [
  { key: "all", label: "All", test: () => true },
  { key: "final", label: "Final", test: (b: StoredBlock) => b.status === "final" },
  { key: "candidate", label: "Candidate", test: (b: StoredBlock) => b.status === "seen_candidate" },
  { key: "ops", label: "With ops", test: (b: StoredBlock) => b.operation_ids.length > 0 },
] as const;

/**
 * Newest-first list of blocks (candidate + final, deduped per block id).
 *
 * Backend: `/v1/blocks` — walks the slot index in desc order and dereferences
 * block ids. We intentionally don't poll aggressively (10s) because the DAG
 * widget on the home page already delivers a real-time view of the head.
 */
export function Blocks() {
  const { client, network } = useAppState();
  const params = useChainParams();
  const paged = usePaged(25);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["key"]>("all");

  const q = useQuery({
    queryKey: [
      "recent-blocks",
      network,
      client.endpoints(),
      paged.cursor,
      paged.limit,
    ],
    queryFn: () => api.recentBlocks(client, paged.limit, paged.cursor),
    refetchInterval: paged.page === 0 ? 10_000 : false,
  });
  useEffect(() => {
    paged.setLastResponse(q.data ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data]);

  const all = q.data?.data ?? [];
  // ponytail: /v1/blocks has no status filter, so this filters the loaded page only.
  const rows = all.filter(FILTERS.find((f) => f.key === filter)!.test);
  const maxOps = Math.max(1, ...all.map((b) => b.operation_ids.length));
  const exportUrl = client.sseUrl("/v1/export/slots.csv");

  return (
    <>
      <Helmet>
        <title>{`Blocks — ${network}`}</title>
      </Helmet>

      <PageHeader
        title="Blocks"
        subtitle={`Every block produced across the ${params.threadCount || 32} threads, newest first.`}
      >
        {paged.page === 0 && <LiveBadge seconds={10} />}
        {exportUrl && (
          <a className="btn h-10 px-4 no-underline text-fg" href={exportUrl} download>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 4v11m0 0-4-4m4 4 4-4M5 20h14" />
            </svg>
            Export slots CSV
          </a>
        )}
      </PageHeader>

      <section className="card p-0 sm:p-0 overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-4 sm:px-6 py-4 sm:py-5">
          <h2 className="text-base sm:text-lg font-semibold mr-auto">Latest blocks</h2>
          <Segmented label="Filter by status" options={FILTERS} value={filter} onChange={setFilter} />
        </div>

        {q.isLoading ? (
          <div className="px-4 sm:px-6 pb-5"><Loading /></div>
        ) : q.isError ? (
          <div className="px-4 sm:px-6 pb-5"><ErrorMsg err={q.error} /></div>
        ) : rows.length === 0 ? (
          <div className="px-4 sm:px-6 pb-5 text-muted text-sm">
            {all.length === 0 ? "No blocks yet." : "No block on this page matches this filter."}
          </div>
        ) : (
          <>
          {/* Mobile: one card per block. */}
          <ul className="md:hidden flex flex-col gap-2 px-4 pb-4">
            {rows.map((b) => {
              const st = BLOCK_STATUS[b.status] ?? BLOCK_STATUS.discarded;
              const ops = b.operation_ids.length;
              const slotTs = b.slot ? slotTimestampMs(b.slot, params) : null;
              return (
                <li key={b.id} className="flex flex-col gap-2.5 p-3.5 rounded-xl bg-bg border border-border">
                  <div className="flex items-center gap-2">
                    <SlotPill slot={b.slot} />
                    <span className="inline-flex items-center gap-1.5 text-xs text-muted">
                      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                      {st.label}
                    </span>
                    <span className="ml-auto text-xs text-muted">
                      {slotTs != null ? formatRelative(slotTs) : formatRelative(b.first_seen_ts_ms)}
                    </span>
                  </div>
                  <BlockLink id={b.id} />
                  <div className="flex items-center gap-3 text-xs text-muted">
                    <AddrLink addr={b.creator} />
                    <span className="ml-auto">
                      <span className={`tabular-nums ${ops ? "text-fg" : ""}`}>{ops}</span> ops
                    </span>
                    <span>
                      <span className="tabular-nums text-fg/80">{b.endorsement_ids.length}</span>/{ENDORSEMENTS_PER_BLOCK} end.
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead className="text-muted text-xs bg-thead">
                <tr>
                  <th className="text-left py-2.5 pl-4 sm:pl-6 pr-3">Block</th>
                  <th className="text-left py-2.5 px-3">Slot</th>
                  <th className="text-left py-2.5 px-3">Creator</th>
                  <th className="text-left py-2.5 px-3">Operations</th>
                  <th className="text-right py-2.5 px-3">Endorsements</th>
                  <th className="text-left py-2.5 px-3">Status</th>
                  <th className="text-right py-2.5 pr-4 sm:pr-6 pl-3 whitespace-nowrap">Produced</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => {
                  const ops = b.operation_ids.length;
                  const st = BLOCK_STATUS[b.status] ?? BLOCK_STATUS.discarded;
                  const slotTs = b.slot ? slotTimestampMs(b.slot, params) : null;
                  return (
                    <tr key={b.id} className="border-t border-border">
                      <td className="py-3 pl-4 sm:pl-6 pr-3">
                        <BlockLink id={b.id} />
                      </td>
                      <td className="py-3 px-3">
                        <SlotPill slot={b.slot} />
                      </td>
                      <td className="py-3 px-3">
                        <AddrLink addr={b.creator} />
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2.5">
                          <span className={`w-7 text-right tabular-nums ${ops ? "text-fg" : "text-muted/50"}`}>
                            {ops}
                          </span>
                          <div className="w-24 h-1 rounded-full bg-border overflow-hidden">
                            <div className="h-full rounded-full bg-accent" style={{ width: `${(ops / maxOps) * 100}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3 text-right tabular-nums text-muted">
                        <span className="text-fg/80">{b.endorsement_ids.length}</span>
                        <span className="text-muted/50">/{ENDORSEMENTS_PER_BLOCK}</span>
                      </td>
                      <td className="py-3 px-3">
                        <span className="inline-flex items-center gap-2 text-[13px] whitespace-nowrap">
                          <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                          {st.label}
                        </span>
                      </td>
                      <td className="py-3 pr-4 sm:pr-6 pl-3 text-right text-muted text-[13px] whitespace-nowrap">
                        {slotTs != null ? formatRelative(slotTs) : formatRelative(b.first_seen_ts_ms)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
        <div className="px-4 sm:px-6 pb-4 border-t border-border">
          <Paginator
            page={paged.page}
            pageSize={paged.pageSize}
            hasMore={paged.hasMore}
            loading={q.isFetching}
            count={all.length}
            onPrev={paged.prev}
            onNext={paged.next}
            prevLabel="← Newer"
            nextLabel="Older →"
          />
        </div>
      </section>
    </>
  );
}

